/**
 * Sign-in. The only file in the app that knows a token exists.
 *
 * Everything else takes a `TokenSource` — an async function returning a string —
 * so moving to a different identity library, or to SPFx where the host supplies
 * the token, is a change here and nowhere else.
 *
 * Two things that cost hours if they are wrong:
 *
 *  - **SharePoint REST and Graph are different resources.** This app calls
 *    `/_api/web/lists/...`, which needs a token for the SharePoint resource.
 *    A Graph token there returns a bare 401 that says nothing about audience.
 *  - **The app registration is a SPA using PKCE, so there is no client secret.**
 *    A public browser app cannot keep one, and any flow that asks for one is the
 *    wrong flow.
 */

import type { TokenSource } from '../api/http.js';

/** The slice of MSAL this app uses. Declared so the module can be tested without a browser. */
export interface MsalLike {
  initialize?(): Promise<void>;
  handleRedirectPromise(): Promise<AuthResult | null>;
  getAllAccounts(): Account[];
  setActiveAccount?(account: Account | null): void;
  getActiveAccount?(): Account | null;
  acquireTokenSilent(request: TokenRequest): Promise<AuthResult>;
  acquireTokenRedirect(request: TokenRequest): Promise<void>;
  loginRedirect(request: TokenRequest): Promise<void>;
  logoutRedirect?(request?: unknown): Promise<void>;
}

export interface Account {
  homeAccountId: string;
  username: string;
  name?: string;
}

export interface AuthResult {
  accessToken: string;
  account: Account | null;
  expiresOn?: Date | null;
}

export interface TokenRequest {
  scopes: readonly string[];
  account?: Account;
}

export class InteractionRequired extends Error {
  constructor(readonly scopes: readonly string[]) {
    super('sign-in required');
    this.name = 'InteractionRequired';
  }
}

/** MSAL signals "the silent path will not work" through these. */
const NEEDS_INTERACTION = new Set([
  'InteractionRequiredAuthError',
  'BrowserAuthError',
  'ClientAuthError',
]);

function needsInteraction(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; errorCode?: string };
  return NEEDS_INTERACTION.has(e.name ?? '') || (e.errorCode ?? '').includes('interaction_required');
}

export interface SessionOptions {
  msal: MsalLike;
  sharePointScopes: readonly string[];
  /** Seconds before expiry at which a cached token is treated as spent. */
  refreshSkewSeconds?: number;
  now?: () => number;
}

/**
 * Holds the signed-in account and hands out tokens.
 *
 * Tokens are cached in memory against their expiry, because the data layer asks
 * for one on every request and MSAL's silent path is not free. The skew means a
 * token is never used in the last minute of its life — a request that starts
 * valid and arrives expired is a 401 that looks like a permissions problem.
 */
export class Session {
  private readonly msal: MsalLike;
  private readonly scopes: readonly string[];
  private readonly skewMs: number;
  private readonly now: () => number;
  private cached: { token: string; expiresAt: number } | null = null;
  private inflight: Promise<string> | null = null;

  constructor(opts: SessionOptions) {
    this.msal = opts.msal;
    this.scopes = opts.sharePointScopes;
    this.skewMs = (opts.refreshSkewSeconds ?? 60) * 1000;
    this.now = opts.now ?? Date.now;
  }

  /** Call once at start-up, before rendering anything that needs data. */
  async initialize(): Promise<Account | null> {
    await this.msal.initialize?.();
    const redirect = await this.msal.handleRedirectPromise();
    if (redirect?.account) {
      this.msal.setActiveAccount?.(redirect.account);
      return redirect.account;
    }
    const existing = this.msal.getActiveAccount?.() ?? this.msal.getAllAccounts()[0] ?? null;
    if (existing) this.msal.setActiveAccount?.(existing);
    return existing;
  }

  get account(): Account | null {
    return this.msal.getActiveAccount?.() ?? this.msal.getAllAccounts()[0] ?? null;
  }

  async signIn(): Promise<void> {
    await this.msal.loginRedirect({ scopes: this.scopes });
  }

  async signOut(): Promise<void> {
    this.cached = null;
    await this.msal.logoutRedirect?.();
  }

  /**
   * A token for SharePoint REST.
   *
   * Concurrent callers share one acquisition: the capture screen can fire a
   * dozen requests at once when it comes back online, and a dozen simultaneous
   * silent renewals is how MSAL ends up throttled.
   */
  readonly getToken: TokenSource = async () => {
    if (this.cached && this.cached.expiresAt - this.skewMs > this.now()) {
      return this.cached.token;
    }
    if (this.inflight) return this.inflight;

    this.inflight = (async () => {
      const account = this.account;
      if (!account) throw new InteractionRequired(this.scopes);
      try {
        const res = await this.msal.acquireTokenSilent({ scopes: this.scopes, account });
        const expiresAt = res.expiresOn ? res.expiresOn.getTime() : this.now() + 30 * 60 * 1000;
        this.cached = { token: res.accessToken, expiresAt };
        return res.accessToken;
      } catch (err) {
        this.cached = null;
        if (needsInteraction(err)) throw new InteractionRequired(this.scopes);
        throw err;
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  };

  /** Sends the user through an interactive prompt after a silent failure. */
  async reauthenticate(): Promise<void> {
    this.cached = null;
    const account = this.account;
    await this.msal.acquireTokenRedirect(account ? { scopes: this.scopes, account } : { scopes: this.scopes });
  }
}
